import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { superAdminAPI } from '../api';
import { CurrencySymbol, CurrencyDisplay } from '../components/CurrencySymbol';
import { 
    Users, Plus, Trash2, Key, CreditCard, History, 
    Menu, X, LogOut, RefreshCw, AlertCircle, CheckCircle, Sun, Moon, Globe, Eye, EyeOff
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';

const SuperAdminDashboard = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('admins');
    const [admins, setAdmins] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Public leaderboard settings
    const [publicSettings, setPublicSettings] = useState({ enabled: true, groups: [] });
    const [publicSettingsLoading, setPublicSettingsLoading] = useState(false);
    
    // Modal states
    const [showCreateAdmin, setShowCreateAdmin] = useState(false);
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [showAddBalance, setShowAddBalance] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    
    // Form states
    const [newUsername, setNewUsername] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [resetPassword, setResetPasswordValue] = useState('');
    const [balanceAmount, setBalanceAmount] = useState('');

    const { token, user, logout } = useAuth();
    const { isDark, toggleTheme } = useTheme();
    const navigate = useNavigate();

    useEffect(() => {
        loadData();
    }, [token]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [adminsData, transactionsData, publicSettingsData] = await Promise.all([
                superAdminAPI.getAdmins(token),
                superAdminAPI.getAllTransactions(token),
                superAdminAPI.getPublicSettings(token)
            ]);
            setAdmins(adminsData);
            setTransactions(transactionsData);
            setPublicSettings(publicSettingsData);
        } catch (err) {
            setError('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleTogglePublicLeaderboard = async () => {
        setPublicSettingsLoading(true);
        try {
            const result = await superAdminAPI.togglePublicLeaderboard(token);
            setPublicSettings(prev => ({ ...prev, enabled: result.enabled }));
            setSuccess(result.message);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to toggle public leaderboard');
        } finally {
            setPublicSettingsLoading(false);
        }
    };

    const handleToggleGroupPublic = async (groupId) => {
        try {
            const result = await superAdminAPI.toggleGroupPublic(token, groupId);
            setPublicSettings(prev => ({
                ...prev,
                groups: prev.groups.map(g => 
                    g.id === groupId ? { ...g, is_public: result.is_public } : g
                )
            }));
            setSuccess(result.message);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to toggle group visibility');
        }
    };

    const handleCreateAdmin = async (e) => {
        e.preventDefault();
        try {
            await superAdminAPI.createAdmin(token, newUsername, newPassword);
            setSuccess('Admin created successfully');
            setShowCreateAdmin(false);
            setNewUsername('');
            setNewPassword('');
            loadData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to create admin');
        }
    };

    const handleDeleteAdmin = async () => {
        try {
            await superAdminAPI.deleteAdmin(token, selectedAdmin.id);
            setSuccess('Admin deleted successfully');
            setShowDeleteConfirm(false);
            setSelectedAdmin(null);
            loadData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to delete admin');
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        try {
            await superAdminAPI.resetAdminPassword(token, selectedAdmin.id, resetPassword);
            setSuccess('Password reset successfully');
            setShowResetPassword(false);
            setResetPasswordValue('');
            setSelectedAdmin(null);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to reset password');
        }
    };

    const handleAddBalance = async (e) => {
        e.preventDefault();
        try {
            await superAdminAPI.addAdminBalance(token, selectedAdmin.id, parseFloat(balanceAmount));
            setSuccess('Balance added successfully');
            setShowAddBalance(false);
            setBalanceAmount('');
            setSelectedAdmin(null);
            loadData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to add balance');
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const formatTime = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    // Clear messages after 3 seconds
    useEffect(() => {
        if (error || success) {
            const timer = setTimeout(() => {
                setError('');
                setSuccess('');
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [error, success]);

    return (
        <div className="dashboard-layout">
            {/* Mobile menu button */}
            <button 
                className="mobile-menu-btn lg:hidden"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="mobile-menu-btn"
            >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            {/* Sidebar overlay */}
            <div 
                className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
                onClick={() => setSidebarOpen(false)}
            />

            {/* Sidebar */}
            <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
                <div className="flex-shrink-0">
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-md bg-neon-green/10 border border-neon-green/30 flex items-center justify-center">
                                <CurrencySymbol size="md" className="text-neon-green" />
                            </div>
                            <div>
                                <h1 className="text-sm font-headings font-bold text-white">VG WALLET</h1>
                                <p className="text-xs text-zinc-500 font-mono">SUPER ADMIN</p>
                            </div>
                        </div>
                        {/* Theme Toggle */}
                        <button
                            onClick={toggleTheme}
                            className="p-2 rounded-md hover:bg-zinc-800 transition-colors"
                            data-testid="theme-toggle-btn"
                            title={isDark ? 'Mode Terang' : 'Mode Gelap'}
                        >
                            {isDark ? (
                                <Sun className="w-5 h-5 text-yellow-400" />
                            ) : (
                                <Moon className="w-5 h-5 text-zinc-600" />
                            )}
                        </button>
                    </div>

                    <nav className="space-y-1">
                        <button
                            onClick={() => { setActiveTab('admins'); setSidebarOpen(false); }}
                            className={`nav-item w-full text-left ${activeTab === 'admins' ? 'active' : ''}`}
                            data-testid="nav-admins"
                        >
                            <Users className="w-5 h-5" />
                            <span>Manage Admins</span>
                        </button>
                        <button
                            onClick={() => { setActiveTab('public'); setSidebarOpen(false); }}
                            className={`nav-item w-full text-left ${activeTab === 'public' ? 'active' : ''}`}
                            data-testid="nav-public"
                        >
                            <Globe className="w-5 h-5" />
                            <span>Klasemen Publik</span>
                        </button>
                        <button
                            onClick={() => { setActiveTab('transactions'); setSidebarOpen(false); }}
                            className={`nav-item w-full text-left ${activeTab === 'transactions' ? 'active' : ''}`}
                            data-testid="nav-transactions"
                        >
                            <History className="w-5 h-5" />
                            <span>All Transactions</span>
                        </button>
                        {/* Logout button moved below transactions */}
                        <button
                            onClick={handleLogout}
                            className="nav-item w-full text-left text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid="logout-btn"
                        >
                            <LogOut className="w-5 h-5" />
                            <span>Logout</span>
                        </button>
                    </nav>
                </div>

                <div className="flex-grow"></div>

                <div className="flex-shrink-0 pt-4 border-t border-zinc-800">
                    <div className="px-3 py-2">
                        <p className="text-xs text-zinc-500 font-mono">LOGGED IN AS</p>
                        <p className="text-sm text-white font-medium">{user?.username}</p>
                    </div>
                </div>
            </aside>

            {/* Main content */}
            <main className="main-content">
                {/* Notifications */}
                {error && (
                    <div className="fixed top-4 right-4 z-50 flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-md text-red-400 animate-slide-up">
                        <AlertCircle className="w-4 h-4" />
                        {error}
                    </div>
                )}
                {success && (
                    <div className="fixed top-4 right-4 z-50 flex items-center gap-2 p-4 bg-neon-green/10 border border-neon-green/20 rounded-md text-neon-green animate-slide-up">
                        <CheckCircle className="w-4 h-4" />
                        {success}
                    </div>
                )}

                {/* Admins Tab */}
                {activeTab === 'admins' && (
                    <div>
                        <div className="page-header flex items-start justify-between">
                            <div>
                                <h1 className="page-title">Manage Admins</h1>
                                <p className="page-subtitle">Create, manage, and allocate balance to admins</p>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    onClick={loadData}
                                    variant="outline"
                                    size="sm"
                                    className="border-zinc-700 text-zinc-400 hover:text-white"
                                    data-testid="refresh-btn"
                                >
                                    <RefreshCw className="w-4 h-4" />
                                </Button>
                                <Button
                                    onClick={() => setShowCreateAdmin(true)}
                                    className="bg-neon-green hover:bg-neon-green/90 text-void-black"
                                    data-testid="create-admin-btn"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Create Admin
                                </Button>
                            </div>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <span className="spinner" />
                            </div>
                        ) : admins.length === 0 ? (
                            <div className="empty-state">
                                <Users className="empty-state-icon mx-auto" />
                                <p>No admins created yet</p>
                            </div>
                        ) : (
                            <div className="grid-cards">
                                {admins.map((admin) => (
                                    <div key={admin.id} className="stat-card card-hover" data-testid={`admin-card-${admin.id}`}>
                                        <div className="flex items-start justify-between mb-4">
                                            <div>
                                                <p className="text-sm text-zinc-500 font-mono uppercase">Admin</p>
                                                <h3 className="text-lg font-headings font-semibold text-white">{admin.username}</h3>
                                            </div>
                                            <div className="badge badge-success">Active</div>
                                        </div>
                                        
                                        <div className="mb-4">
                                            <p className="text-xs text-zinc-500 mb-1">Balance</p>
                                            <CurrencyDisplay amount={admin.balance} size="lg" className="text-neon-green" />
                                        </div>

                                        <div className="text-xs text-zinc-600 mb-4">
                                            Created: {formatDate(admin.created_at)}
                                        </div>

                                        <div className="flex gap-2">
                                            <Button
                                                onClick={() => { setSelectedAdmin(admin); setShowAddBalance(true); }}
                                                size="sm"
                                                className="flex-1 bg-neon-green/10 text-neon-green hover:bg-neon-green/20 border border-neon-green/20"
                                                data-testid={`add-balance-btn-${admin.id}`}
                                            >
                                                <CreditCard className="w-3 h-3 mr-1" />
                                                Add Balance
                                            </Button>
                                            <Button
                                                onClick={() => { setSelectedAdmin(admin); setShowResetPassword(true); }}
                                                size="sm"
                                                variant="outline"
                                                className="border-zinc-700 text-zinc-400 hover:text-white"
                                                data-testid={`reset-password-btn-${admin.id}`}
                                            >
                                                <Key className="w-3 h-3" />
                                            </Button>
                                            <Button
                                                onClick={() => { setSelectedAdmin(admin); setShowDeleteConfirm(true); }}
                                                size="sm"
                                                variant="outline"
                                                className="border-red-500/20 text-red-400 hover:bg-red-500/10"
                                                data-testid={`delete-admin-btn-${admin.id}`}
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Public Leaderboard Settings Tab */}
                {activeTab === 'public' && (
                    <div>
                        <div className="page-header flex items-start justify-between">
                            <div>
                                <h1 className="page-title">Klasemen Publik</h1>
                                <p className="page-subtitle">Kelola tampilan klasemen untuk publik (tanpa login)</p>
                            </div>
                            <Button
                                onClick={loadData}
                                variant="outline"
                                size="sm"
                                className="border-zinc-700 text-zinc-400 hover:text-white"
                            >
                                <RefreshCw className="w-4 h-4" />
                            </Button>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <span className="spinner" />
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {/* Global Toggle */}
                                <div className="stat-card">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="text-lg font-headings font-semibold text-white flex items-center gap-2">
                                                <Globe className="w-5 h-5 text-neon-green" />
                                                Tampilkan Klasemen Publik
                                            </h3>
                                            <p className="text-sm text-zinc-500 mt-1">
                                                Aktifkan agar pengunjung dapat melihat klasemen tanpa login
                                            </p>
                                        </div>
                                        <Button
                                            onClick={handleTogglePublicLeaderboard}
                                            disabled={publicSettingsLoading}
                                            className={`${publicSettings.enabled 
                                                ? 'bg-neon-green hover:bg-neon-green/90 text-void-black' 
                                                : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400'}`}
                                            data-testid="toggle-public-leaderboard-btn"
                                        >
                                            {publicSettingsLoading ? (
                                                <span className="spinner w-4 h-4" />
                                            ) : publicSettings.enabled ? (
                                                <>
                                                    <Eye className="w-4 h-4 mr-2" />
                                                    AKTIF
                                                </>
                                            ) : (
                                                <>
                                                    <EyeOff className="w-4 h-4 mr-2" />
                                                    NONAKTIF
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>

                                {/* Group Selection */}
                                {publicSettings.enabled && (
                                    <div className="stat-card">
                                        <h3 className="text-lg font-headings font-semibold text-white mb-4">
                                            Pilih Grup yang Ditampilkan
                                        </h3>
                                        <p className="text-sm text-zinc-500 mb-4">
                                            Centang grup yang ingin ditampilkan di klasemen publik. Jika tidak ada yang dicentang, semua grup akan ditampilkan.
                                        </p>
                                        
                                        {publicSettings.groups.length === 0 ? (
                                            <div className="text-center py-8 text-zinc-500">
                                                <Globe className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                                <p>Belum ada grup</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                {publicSettings.groups.map((group) => (
                                                    <div 
                                                        key={group.id}
                                                        className={`flex items-center justify-between p-3 rounded-lg border ${
                                                            group.is_public 
                                                                ? 'bg-neon-green/10 border-neon-green/30' 
                                                                : 'bg-zinc-900 border-zinc-800'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <button
                                                                onClick={() => handleToggleGroupPublic(group.id)}
                                                                className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                                                                    group.is_public
                                                                        ? 'bg-neon-green border-neon-green text-void-black'
                                                                        : 'border-zinc-600 text-transparent hover:border-neon-green/50'
                                                                }`}
                                                                data-testid={`toggle-group-public-${group.id}`}
                                                            >
                                                                {group.is_public && '✓'}
                                                            </button>
                                                            <div>
                                                                <p className="text-white font-medium">{group.name}</p>
                                                                <p className="text-xs text-zinc-500">Admin: {group.admin_name}</p>
                                                            </div>
                                                        </div>
                                                        <div className={`text-xs px-2 py-1 rounded ${
                                                            group.is_public 
                                                                ? 'bg-neon-green/20 text-neon-green' 
                                                                : 'bg-zinc-800 text-zinc-500'
                                                        }`}>
                                                            {group.is_public ? 'Publik' : 'Privat'}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        
                                        <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                                            <p className="text-sm text-yellow-400">
                                                💡 <strong>Tips:</strong> Jika tidak ada grup yang dicentang, semua grup akan tampil di klasemen publik secara default.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Transactions Tab */}
                {activeTab === 'transactions' && (
                    <div>
                        <div className="page-header flex items-start justify-between">
                            <div>
                                <h1 className="page-title">All Transactions</h1>
                                <p className="page-subtitle">View all system-wide transactions</p>
                            </div>
                            <Button
                                onClick={loadData}
                                variant="outline"
                                size="sm"
                                className="border-zinc-700 text-zinc-400 hover:text-white"
                            >
                                <RefreshCw className="w-4 h-4" />
                            </Button>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <span className="spinner" />
                            </div>
                        ) : transactions.length === 0 ? (
                            <div className="empty-state">
                                <History className="empty-state-icon mx-auto" />
                                <p>No transactions yet</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto rounded-lg border border-zinc-800">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Time</th>
                                            <th>From</th>
                                            <th>To</th>
                                            <th>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transactions.map((tx) => (
                                            <tr key={tx.id} data-testid={`transaction-row-${tx.id}`}>
                                                <td>{formatDate(tx.created_at)}</td>
                                                <td>{formatTime(tx.created_at)}</td>
                                                <td className="text-zinc-300">{tx.from_username}</td>
                                                <td className="text-zinc-300">{tx.to_username}</td>
                                                <td>
                                                    <CurrencyDisplay amount={tx.amount} size="sm" className="text-neon-green" />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </main>

            {/* Create Admin Modal */}
            <Dialog open={showCreateAdmin} onOpenChange={setShowCreateAdmin}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="font-headings">Create New Admin</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateAdmin} className="space-y-4">
                        <div>
                            <Label htmlFor="adminUsername" className="form-label">Username</Label>
                            <Input
                                id="adminUsername"
                                value={newUsername}
                                onChange={(e) => setNewUsername(e.target.value)}
                                placeholder="Enter username"
                                className="bg-zinc-800 border-zinc-700"
                                data-testid="create-admin-username"
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="adminPassword" className="form-label">Password</Label>
                            <Input
                                id="adminPassword"
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Enter password"
                                className="bg-zinc-800 border-zinc-700"
                                data-testid="create-admin-password"
                                required
                            />
                        </div>
                        <div className="flex gap-2 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowCreateAdmin(false)}
                                className="flex-1 border-zinc-700"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="flex-1 bg-neon-green hover:bg-neon-green/90 text-void-black"
                                data-testid="create-admin-submit"
                            >
                                Create Admin
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Reset Password Modal */}
            <Dialog open={showResetPassword} onOpenChange={setShowResetPassword}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="font-headings">Reset Password</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleResetPassword} className="space-y-4">
                        <p className="text-sm text-zinc-400">
                            Reset password for admin: <span className="text-white font-medium">{selectedAdmin?.username}</span>
                        </p>
                        <div>
                            <Label htmlFor="resetPassword" className="form-label">New Password</Label>
                            <Input
                                id="resetPassword"
                                type="password"
                                value={resetPassword}
                                onChange={(e) => setResetPasswordValue(e.target.value)}
                                placeholder="Enter new password"
                                className="bg-zinc-800 border-zinc-700"
                                data-testid="reset-password-input"
                                required
                            />
                        </div>
                        <div className="flex gap-2 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowResetPassword(false)}
                                className="flex-1 border-zinc-700"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="flex-1 bg-neon-green hover:bg-neon-green/90 text-void-black"
                                data-testid="reset-password-submit"
                            >
                                Reset Password
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Add Balance Modal */}
            <Dialog open={showAddBalance} onOpenChange={setShowAddBalance}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="font-headings">Add Balance</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddBalance} className="space-y-4">
                        <p className="text-sm text-zinc-400">
                            Add balance to admin: <span className="text-white font-medium">{selectedAdmin?.username}</span>
                        </p>
                        <p className="text-sm text-zinc-500">
                            Current balance: <CurrencyDisplay amount={selectedAdmin?.balance || 0} size="sm" className="text-neon-green" />
                        </p>
                        <div>
                            <Label htmlFor="balanceAmount" className="form-label">Amount</Label>
                            <div className="relative">
                                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                                    <CurrencySymbol size="sm" className="text-zinc-500" />
                                </div>
                                <Input
                                    id="balanceAmount"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={balanceAmount}
                                    onChange={(e) => setBalanceAmount(e.target.value)}
                                    placeholder="0.00"
                                    className="bg-zinc-800 border-zinc-700 pl-8"
                                    data-testid="add-balance-input"
                                    required
                                />
                            </div>
                        </div>
                        <div className="flex gap-2 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowAddBalance(false)}
                                className="flex-1 border-zinc-700"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="flex-1 bg-neon-green hover:bg-neon-green/90 text-void-black"
                                data-testid="add-balance-submit"
                            >
                                Add Balance
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-headings">Delete Admin</AlertDialogTitle>
                        <AlertDialogDescription className="text-zinc-400">
                            Are you sure you want to delete admin <span className="text-white font-medium">{selectedAdmin?.username}</span>?
                            This will also delete all users and groups created by this admin.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="border-zinc-700">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteAdmin}
                            className="bg-red-500 hover:bg-red-600"
                            data-testid="confirm-delete-btn"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default SuperAdminDashboard;
